/**
 * Master Data Edit (setup)
 * Entry point used by initRegistry.
 */

import { MasterDataEditController } from "./master-data-edit-handler";
import { MasterDataEditListener } from "./master-data-edit-listener";
import { Roles } from "../constants/roles";
import { getConfigScript, getEditorRoots, qs, type FormDataObject } from "./master-data-edit-form";

type MasterDataEditContextRecord = FormDataObject;

interface MasterDataEditContext {
    resource: string;
    urls: Record<string, string>;
    item?: MasterDataEditContextRecord | null;
    defaults?: MasterDataEditContextRecord;
    specDefaults?: MasterDataEditContextRecord;
    [key: string]: unknown;
}

type ControllerMap = Map<string, MasterDataEditController>;

export class MasterDataEditSetup {
    public createControllersMap(): ControllerMap {
        return new Map();
    }

    public getGlobalControllers(): ControllerMap {
        const existing = Reflect.get(globalThis, "__masterDataEditControllers");
        if (existing instanceof Map) {
            return existing as ControllerMap;
        }
        const created: ControllerMap = new Map();
        Reflect.set(globalThis, "__masterDataEditControllers", created);
        return created;
    }

    public init(): void {
        const roots = getEditorRoots(Roles.MASTER_DATA_EDITOR, document);
        if (!roots || roots.length === 0) return;

        const controllers = this.createControllersMap();
        const globalControllers = this.getGlobalControllers();

        roots.forEach((root) => {
            const configEl = getConfigScript(root, Roles.MASTER_DATA_EDIT_CONFIG);
            if (!(configEl instanceof HTMLScriptElement)) return;

            let ctx: MasterDataEditContext | null = null;
            try {
                const parsed = JSON.parse(configEl.textContent || "") as unknown;
                if (parsed && typeof parsed === "object") {
                    const candidate = parsed as MasterDataEditContext;
                    if (candidate.resource && candidate.urls) {
                        ctx = candidate;
                    }
                }
            } catch {
                return;
            }

            if (!ctx || !ctx.resource || !ctx.urls) return;

            const key = root.dataset.masterEditor || ctx.resource;
            if (!key) return;

            const modal = qs(document, `[data-role="${Roles.MASTER_DATA_MODAL}"][data-master-editor="${key}"]`);

            let controller = globalControllers.get(key) || null;

            if (!controller) {
                controller = new MasterDataEditController(ctx, modal instanceof HTMLElement ? modal : null);
                controller.bindModalEvents();
                globalControllers.set(key, controller);
            } else {
                controller.ctx = ctx;
                controller.urls = ctx.urls || {};
            }

            controllers.set(key, controller);
        });

        if (controllers.size === 0) return;

        const listener = new MasterDataEditListener();
        listener.register(controllers);
    }
}

export function initMasterDataEdit(): void {
    new MasterDataEditSetup().init();
}
